import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { config } from '../config';
import api from '../services/api';
import { fechaLimiteDesdeInput } from '../utils/fechaCalendario';
import VersionControl from './VersionControl';
import { isPdfsEntregaPath } from '../utils/folderPaths';
import './ParteAsignadaGestion.css';

const ParteAsignadaGestion = () => {
  const { parteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError, showConfirm } = useToast();
  
  const [parte, setParte] = useState(null);
  const [expediente, setExpediente] = useState(null);
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({
    descripcion: '',
    fechaLimite: '',
    estado: ''
  });
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showVersionControl, setShowVersionControl] = useState(false);
  const [selectedArchivoId, setSelectedArchivoId] = useState(null);
  const [assignedFolderPath, setAssignedFolderPath] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  const normalizePathToken = useCallback((value = '') => (
    value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
  ), []);

  const getParteFolderCandidates = useCallback(() => {
    if (!parte?.descripcion) return [];

    const base = parte.descripcion.trim();
    const withUnderscore = base.replace(/\s+/g, '_');
    const withoutSpecialChars = base.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');

    return [base, withUnderscore, withoutSpecialChars]
      .map(candidate => candidate.trim())
      .filter(Boolean);
  }, [parte?.descripcion]);

  const resolveAssignedFolder = useCallback((availableFolders = []) => {
    if (!availableFolders.length) return '';

    const candidates = getParteFolderCandidates();
    const normalizedCandidates = candidates.map(normalizePathToken);

    // Match exact name (case-insensitive) first.
    const exactMatch = availableFolders.find(folder =>
      candidates.some(candidate => folder.name.toLowerCase() === candidate.toLowerCase())
    );
    if (exactMatch) return exactMatch.path || exactMatch.name;

    // Match normalized names, supporting legacy prefixes like "01_" in folder names.
    const normalizedMatch = availableFolders.find(folder => {
      const folderNormalized = normalizePathToken(folder.name);
      const withoutLeadingIndex = normalizePathToken(folder.name.replace(/^\d+_/, ''));
      return normalizedCandidates.some(candidate =>
        folderNormalized === candidate ||
        withoutLeadingIndex === candidate ||
        folderNormalized.includes(candidate)
      );
    });

    return normalizedMatch ? (normalizedMatch.path || normalizedMatch.name) : '';
  }, [getParteFolderCandidates, normalizePathToken]);

  const isWithinAssignedFolder = useCallback((pathValue = '') => {
    if (!assignedFolderPath) return false;
    return pathValue === assignedFolderPath || pathValue.startsWith(`${assignedFolderPath}/`);
  }, [assignedFolderPath]);

  const loadParteData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar datos de la parte
      const parteResponse = await api.get(`/partes/${parteId}`);
      const parteData = parteResponse.data;
      
      // Verificar que el usuario estÃ¡ asignado a esta parte
      if (parteData.asignado?.id !== user?.id) {
        setError('No tienes permisos para gestionar esta parte');
        return;
      }

      setParte(parteData);

      // Cargar datos del expediente
      const expedienteResponse = await api.get(`/expedientes/${parteData.expedienteId}`);
      setExpediente(expedienteResponse.data);

      // Inicializar formulario de ediciÃ³n
      setEditFormData({
        descripcion: parteData.descripcion,
        fechaLimite: parteData.fechaLimite.split('T')[0],
        estado: parteData.estado
      });

    } catch (error) {
      console.error('Error cargando datos de la parte:', error);
      setError('Error al cargar los datos de la parte');
    } finally {
      setLoading(false);
    }
  }, [parteId, user?.id]);

  const canEditCurrentFolder = useCallback(() => {
    if (isPdfsEntregaPath(currentPath)) {
      return true;
    }

    // Solo puede editar la carpeta de su parte asignada si no estÃ¡ completada
    if (parte?.estado === 'COMPLETADA') {
      return false;
    }

    if (assignedFolderPath) {
      return isWithinAssignedFolder(currentPath);
    }

    // Fallback para estructuras antiguas donde aÃºn no se resolviÃ³ la carpeta.
    const parteFolder = parte?.descripcion.replace(/\s+/g, '_');
    return currentPath === parteFolder || currentPath.startsWith(`${parteFolder}/`);
  }, [parte?.estado, parte?.descripcion, currentPath, assignedFolderPath, isWithinAssignedFolder]);

  const loadFolderContent = useCallback(async () => {
    try {
      // Cargar estructura real de carpetas
      const response = await fetch(`${config.apiUrl}/folders/expediente/${parte.expedienteId}?path=${encodeURIComponent(currentPath)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar contenido de carpeta');
      }

      const data = await response.json();
      let visibleFolders = data.folders || [];

      // Resolver carpeta asignada en raÃ­z y entrar automÃ¡ticamente si existe.
      if (!currentPath && !assignedFolderPath && visibleFolders.length > 0) {
        const resolvedFolder = resolveAssignedFolder(visibleFolders);
        if (resolvedFolder) {
          setAssignedFolderPath(resolvedFolder);
          setCurrentPath(resolvedFolder);
          return;
        }
      }

      // Si ya conocemos la carpeta asignada, en raÃ­z solo mostrar esa carpeta.
      if (!currentPath && assignedFolderPath) {
        visibleFolders = visibleFolders.filter(folder =>
          (folder.path || folder.name) === assignedFolderPath
        );
      }

      setFolders(visibleFolders);
      
               // Cargar archivos desde la base de datos para tener acceso al control de versiones
         try {
           const archivosResponse = await api.get(`/archivos/expediente/${parte.expedienteId}`);
           const archivosDB = archivosResponse.data.archivos || [];
           
           // Filtrar archivos que correspondan a la carpeta actual
           const archivosEnCarpeta = archivosDB.filter(archivo => {
             const archivoPath = archivo.ruta.split('/');
             const currentPathParts = currentPath ? currentPath.split('/') : [];
             
             // Verificar si el archivo estÃ¡ en la carpeta actual
             return archivoPath.length > currentPathParts.length && 
                    currentPathParts.every((part, index) => archivoPath[index] === part);
           });
        
                 // Combinar informaciÃ³n del sistema de archivos con la base de datos
         const processedFiles = (data.files || []).map(file => {
           const archivoDB = archivosEnCarpeta.find(db => 
             db.nombreOriginal === file.name || 
             db.ruta.endsWith(file.name)
           );
           
           const processedFile = {
             ...file,
             id: archivoDB?.id, // ID para control de versiones
             canEdit: canEditCurrentFolder() && parte?.estado !== 'COMPLETADA',
             version: archivoDB?.version || 1
           };
           

           
           return processedFile;
         });
        
        setFiles(processedFiles);
      } catch (archivosError) {
        console.warn('No se pudieron cargar archivos desde la base de datos:', archivosError);
        // Fallback: usar solo archivos del sistema de archivos
        const processedFiles = (data.files || []).map(file => ({
          ...file,
          canEdit: canEditCurrentFolder() && parte?.estado !== 'COMPLETADA'
        }));
        setFiles(processedFiles);
      }
    } catch (error) {
      console.error('Error cargando contenido de carpeta:', error);
      setError('Error al cargar el contenido de la carpeta');
    }
  }, [parte?.expedienteId, currentPath, canEditCurrentFolder, assignedFolderPath, resolveAssignedFolder]);

  useEffect(() => {
    loadParteData();
  }, [loadParteData]);

  useEffect(() => {
    if (parte) {
      loadFolderContent();
    }
  }, [loadFolderContent, parte]);

  const navigateToFolder = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  const navigateBack = () => {
    if (!currentPath) return;
    
    const pathParts = currentPath.split('/').filter(part => part);
    if (pathParts.length === 0) {
      setCurrentPath('');
    } else {
      pathParts.pop();
      setCurrentPath(pathParts.join('/'));
    }
  };

  const getBreadcrumb = () => {
    const parts = currentPath.split('/').filter(part => part);
    return parts;
  };

  const isVisualizableFile = (filename) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    const visualizableExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'txt', 'html', 'htm'];
    return visualizableExtensions.includes(extension);
  };

  const handleFileClick = (file) => {
    if (isVisualizableFile(file.name)) {
      // Abrir vista previa
      const baseUrl = config.apiUrl.replace('/api', '');
      const encodedPath = encodeURIComponent(currentPath);
      const encodedFileName = encodeURIComponent(file.name);
      // Usar el nombre real de la carpeta del expediente (mismo formato que backend)
      const numeroExpediente = expediente?.numero?.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') || 'EXP';
      const nombreCliente = expediente?.cliente?.nombre?.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') || '';
      const tipoExpediente = expediente?.tipo?.nombre?.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') || '';
      const carpetaReal = `${numeroExpediente}_${nombreCliente}_${tipoExpediente}`;
      const fileUrl = `${baseUrl}/uploads/${carpetaReal}/${encodedPath}/${encodedFileName}`;
      window.open(fileUrl, '_blank');
    } else {
      // Descargar archivo
      downloadFile(file);
    }
  };

  const downloadFile = async (file) => {
    try {
      const baseUrl = config.apiUrl.replace('/api', '');
      const encodedPath = encodeURIComponent(currentPath);
      const encodedFileName = encodeURIComponent(file.name);
      // Usar el nombre real de la carpeta del expediente (mismo formato que backend)
      const numeroExpediente = expediente?.numero?.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') || 'EXP';
      const nombreCliente = expediente?.cliente?.nombre?.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') || '';
      const tipoExpediente = expediente?.tipo?.nombre?.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_') || '';
      const carpetaReal = `${numeroExpediente}_${nombreCliente}_${tipoExpediente}`;
      const fileUrl = `${baseUrl}/uploads/${carpetaReal}/${encodedPath}/${encodedFileName}`;
      
      // Descargar el archivo usando fetch
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Error al descargar: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Crear elemento de descarga
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar el objeto URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      showError('Error al descargar el archivo. Intentalo de nuevo.');
    }
  };

  const deleteFile = async (file) => {
    const confirmed = await showConfirm(`\u00bfEstas seguro de que quieres eliminar el archivo "${file.name}"?`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`${config.apiUrl}/folders/expediente/${expediente.id}/file?fileName=${encodeURIComponent(file.name)}&folderPath=${encodeURIComponent(currentPath)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al eliminar el archivo');
      }
      
      // Recargar contenido
      loadFolderContent();
      showSuccess('Archivo eliminado exitosamente');
    } catch (error) {
      console.error('Error eliminando archivo:', error);
      showError('Error al eliminar el archivo: ' + error.message);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;

    try {
      setUploading(true);
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('archivos', file);
      });

      // Subir archivos al servidor
      const response = await fetch(
        `${config.apiUrl}/folders/expediente/${expediente.id}/upload?folderPath=${encodeURIComponent(currentPath)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || 'Error al subir archivos');
      }
      
      // Recargar contenido
      loadFolderContent();
      setSelectedFiles([]);
      setShowUploadForm(false);
      
      showSuccess('Archivos subidos exitosamente');
    } catch (error) {
      console.error('Error subiendo archivos:', error);
      showError('Error al subir los archivos: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEditParte = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editFormData,
        ...(editFormData.fechaLimite
          ? { fechaLimite: fechaLimiteDesdeInput(editFormData.fechaLimite) }
          : {})
      };
      await api.patch(`/partes/${parteId}`, payload);
      await loadParteData();
      setShowEditForm(false);
      showSuccess('Parte actualizada exitosamente');
      
      // Notificar al componente padre para que recargue los datos del expediente
      // Esto actualizarÃ¡ el contador de partes pendientes si cambiÃ³ el estado
      window.dispatchEvent(new CustomEvent('parteActualizada', { 
        detail: { expedienteId: parte?.expedienteId } 
      }));
    } catch (error) {
      console.error('Error actualizando parte:', error);
      showError('Error al actualizar la parte');
    }
  };

  const handleMarkAsCompleted = async () => {
    try {
      // Guardar preferencia si estÃ¡ marcada
      if (dontShowAgain) {
        localStorage.setItem('dontShowCompleteModal', 'true');
      }

      await api.patch(`/partes/${parteId}`, {
        estado: 'COMPLETADA',
        fechaCompletado: new Date().toISOString()
      });
      await loadParteData();
      setShowCompleteModal(false);
      showSuccess('Parte marcada como completada exitosamente');
      
      // Notificar al componente padre para que recargue los datos del expediente
      // Esto actualizarÃ¡ el contador de partes pendientes
      window.dispatchEvent(new CustomEvent('parteCompletada', { 
        detail: { expedienteId: parte?.expedienteId } 
      }));
    } catch (error) {
      console.error('Error marcando parte como completada:', error);
      showError('Error al marcar la parte como completada');
    }
  };

  // Funciones para el control de versiones
  const handleOpenVersionControl = (archivoId) => {
    setSelectedArchivoId(archivoId);
    setShowVersionControl(true);
  };

  const handleCloseVersionControl = () => {
    setShowVersionControl(false);
    setSelectedArchivoId(null);
  };

  const handleVersionCreated = () => {
    loadFolderContent(); // Recargar contenido de la carpeta
  };

  // FunciÃ³n para actualizar avance del archivo
  const handleUpdateProgress = (archivoId, fileName) => {
    // Crear un input de archivo oculto
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar,.dwg';
    fileInput.style.display = 'none';
    
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      try {
        // Mostrar mensaje de confirmaciÃ³n
        const confirmMessage = `Â¿EstÃ¡s seguro de que quieres actualizar "${fileName}"?\n\nEsto crearÃ¡ una nueva versiÃ³n del archivo.`;
        const confirmed = await showConfirm(confirmMessage);
        if (!confirmed) return;
        
        // Crear barra de progreso
        const progressContainer = document.createElement('div');
        progressContainer.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border: 2px solid #007bff;
          border-radius: 10px;
          padding: 20px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          z-index: 10000;
          min-width: 300px;
          text-align: center;
        `;
        
        const progressTitle = document.createElement('div');
        progressTitle.textContent = `Actualizando ${fileName}...`;
        progressTitle.style.cssText = `
          font-weight: bold;
          margin-bottom: 15px;
          color: #333;
        `;
        
        const progressBar = document.createElement('div');
        progressBar.style.cssText = `
          width: 100%;
          height: 20px;
          background-color: #f0f0f0;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 10px;
        `;
        
        const progressFill = document.createElement('div');
        progressFill.style.cssText = `
          height: 100%;
          background: linear-gradient(90deg, #007bff, #0056b3);
          width: 0%;
          transition: width 0.3s ease;
          border-radius: 10px;
        `;
        
        const progressText = document.createElement('div');
        progressText.textContent = '0%';
        progressText.style.cssText = `
          font-size: 14px;
          color: #666;
        `;
        
        progressBar.appendChild(progressFill);
        progressContainer.appendChild(progressTitle);
        progressContainer.appendChild(progressBar);
        progressContainer.appendChild(progressText);
        document.body.appendChild(progressContainer);
        
        // Crear FormData y subir
        const formData = new FormData();
        formData.append('archivo', file);
        
        // Llamar al API de versiones para crear nueva versiÃ³n con progreso
        const response = await api.post(`/versiones/archivo/${archivoId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            progressFill.style.width = `${percentCompleted}%`;
            progressText.textContent = `${percentCompleted}%`;
          }
        });
        
        // Completar la barra de progreso
        progressFill.style.width = '100%';
        progressText.textContent = '100%';
        
        // Esperar un momento para mostrar el 100%
        setTimeout(() => {
          document.body.removeChild(progressContainer);
        }, 500);
        
        // Recargar contenido
        loadFolderContent();
        
        // Mostrar mensaje informativo segÃºn la respuesta del servidor
        const mensaje = response.data.message || `Archivo "${fileName}" actualizado exitosamente`;
        showSuccess(mensaje);
        
      } catch (error) {
        console.error('Error actualizando archivo:', error);
        
        // Manejar diferentes tipos de errores con mensajes especÃ­ficos
        let errorMessage = 'Error al actualizar el archivo';
        
        if (error.response?.status === 400) {
          const errorData = error.response.data;
          if (errorData?.error?.includes('lÃ­mite mÃ¡ximo de 3 versiones')) {
            errorMessage = 'âš ï¸ LÃ­mite de versiones alcanzado\n\nEste archivo ya tiene el mÃ¡ximo de 3 versiones permitidas. El sistema mantendrÃ¡ automÃ¡ticamente solo las 3 versiones mÃ¡s recientes.';
          } else if (errorData?.error?.includes('No se proporcionÃ³ ningÃºn archivo')) {
            errorMessage = 'âŒ No se seleccionÃ³ ningÃºn archivo\n\nPor favor, selecciona un archivo antes de continuar.';
          } else if (errorData?.error?.includes('Tipo de archivo no permitido')) {
            errorMessage = 'âŒ Tipo de archivo no permitido\n\nEl tipo de archivo seleccionado no estÃ¡ permitido. Por favor, selecciona un archivo vÃ¡lido (PDF, Word, Excel, imÃ¡genes, etc.).';
          } else {
            errorMessage = `âŒ Error de validaciÃ³n\n\n${errorData?.error || error.message}`;
          }
        } else if (error.response?.status === 403) {
          errorMessage = 'ðŸ”’ Acceso denegado\n\nNo tienes permisos para modificar este archivo.';
        } else if (error.response?.status === 404) {
          errorMessage = 'âŒ Archivo no encontrado\n\nEl archivo que intentas actualizar no existe.';
        } else if (error.response?.status === 413) {
          errorMessage = 'ðŸ“ Archivo demasiado grande\n\nEl archivo seleccionado excede el lÃ­mite de 50MB. Por favor, selecciona un archivo mÃ¡s pequeÃ±o.';
        } else {
          errorMessage = `âŒ Error inesperado\n\n${error.message}`;
        }
        
        showError(errorMessage);
        
        // Remover barra de progreso en caso de error
        const progressContainer = document.querySelector('[style*="position: fixed"]');
        if (progressContainer) {
          document.body.removeChild(progressContainer);
        }
      } finally {
        // Limpiar el input
        document.body.removeChild(fileInput);
      }
    };
    
    // Agregar el input al DOM y hacer clic
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  const resetCompleteModalPreference = () => {
    localStorage.removeItem('dontShowCompleteModal');
    showSuccess('Preferencia reseteada. Las confirmaciones se mostrarÃ¡n de nuevo.');
  };

  const formatFechaCorta = (fecha) => {
    if (!fecha) return 'â€”';
    return new Date(fecha).toLocaleDateString('es-PE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatRelativeDate = (date) => {
    if (!date) return '';
    const now = new Date();
    const d = new Date(date);
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Editado hoy';
    if (diffDays === 1) return 'Editado ayer';
    if (diffDays < 7) return `Editado hace ${diffDays} dÃ­as`;
    return d.toLocaleDateString('es-PE', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getFileExtension = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext ? ext.toUpperCase() : 'ARCHIVO';
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      PENDIENTE: 'Pendiente',
      EN_PROGRESO: 'En proceso',
      COMPLETADA: 'Completado',
      OBSERVADO: 'Observado'
    };
    return labels[estado] || estado;
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    const name = newFolderName.trim();
    if (!name) return;

    try {
      setCreatingFolder(true);
      const response = await fetch(
        `${config.apiUrl}/folders/expediente/${expediente.id}/folder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ folderPath: currentPath, folderName: name })
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Error al crear la carpeta');
      }

      setShowNewFolderModal(false);
      setNewFolderName('');
      loadFolderContent();
      showSuccess('Carpeta creada');
    } catch (error) {
      showError(error.message);
    } finally {
      setCreatingFolder(false);
    }
  };

  const filteredFolders = folders.filter(f =>
    !searchTerm || f.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFiles = files.filter(f =>
    !searchTerm || f.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canEditCurrent = canEditCurrentFolder();

  const formatDateShort = (date) => {
    if (!date) return '\u2014';
    return new Date(date).toLocaleDateString('es-PE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getFileIconClass = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const map = {
      pdf: { icon: 'fa-file-pdf', tone: 'pdf' },
      jpg: { icon: 'fa-file-image', tone: 'image' },
      jpeg: { icon: 'fa-file-image', tone: 'image' },
      png: { icon: 'fa-file-image', tone: 'image' },
      gif: { icon: 'fa-file-image', tone: 'image' },
      bmp: { icon: 'fa-file-image', tone: 'image' },
      webp: { icon: 'fa-file-image', tone: 'image' },
      doc: { icon: 'fa-file-word', tone: 'doc' },
      docx: { icon: 'fa-file-word', tone: 'doc' },
      xls: { icon: 'fa-file-excel', tone: 'sheet' },
      xlsx: { icon: 'fa-file-excel', tone: 'sheet' },
      dwg: { icon: 'fa-drafting-compass', tone: 'cad' },
      dxf: { icon: 'fa-drafting-compass', tone: 'cad' }
    };
    return map[ext] || { icon: 'fa-file', tone: '' };
  };

  const canEditFolder = (folderName) => {
    const candidatePath = currentPath ? `${currentPath}/${folderName}` : folderName;
    if (isPdfsEntregaPath(candidatePath) || isPdfsEntregaPath(currentPath)) {
      return true;
    }

    // Solo puede editar la carpeta de su parte asignada si no estÃ¡ completada
    if (parte?.estado === 'COMPLETADA') {
      return false;
    }

    if (assignedFolderPath) {
      return isWithinAssignedFolder(candidatePath) || isWithinAssignedFolder(currentPath);
    }

    const parteFolder = parte?.descripcion.replace(/\s+/g, '_');
    return folderName === parteFolder || currentPath.includes(parteFolder);
  };

  if (loading) {
    return (
      <div className="pag">
        <div className="pag__loading">
          <div className="pag__spinner" />
          <p>Cargando tarea...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pag">
        <div className="pag__error">{error}</div>
      </div>
    );
  }

  if (!parte) {
    return (
      <div className="pag">
        <div className="pag__error">No se encontró la parte asignada</div>
      </div>
    );
  }

  const estadoClass = parte.estado.toLowerCase();

  return (
    <div className="pag">
      <header className="pag__header">
        <div className="pag__header-left">
          <div className="pag__title-row">
            <h1 className="pag__title">{parte.descripcion}</h1>
            <span className={`pag__estado pag__estado--${estadoClass}`}>
              <span className="pag__estado-dot" aria-hidden="true" />
              {getEstadoLabel(parte.estado)}
            </span>
          </div>
          <div className="pag__meta">
            <strong>{expediente?.numero}</strong>
            <span className="pag__meta-sep">&middot;</span>
            <span>Cliente: {expediente?.cliente?.nombre}</span>
            <span className="pag__meta-sep">&middot;</span>
            <span>Asignado: {user?.nombre}</span>
            <span className="pag__meta-sep">&middot;</span>
            <span>Vence: {formatFechaCorta(parte.fechaLimite)}</span>
          </div>
        </div>
        {parte.estado !== 'COMPLETADA' && (
          <button
            type="button"
            className="pag__btn-complete"
            onClick={() => {
              const dontShow = localStorage.getItem('dontShowCompleteModal') === 'true';
              if (dontShow) {
                handleMarkAsCompleted();
              } else {
                setShowCompleteModal(true);
              }
            }}
          >
            Completar tarea
          </button>
        )}
      </header>

      <div className="pag__topbar">
        <nav className="pag__breadcrumb" aria-label="Ruta">
          <button
            type="button"
            className="pag__breadcrumb-link pag__breadcrumb-link--back"
            onClick={() => navigate('/dashboard')}
          >
            <i className="fas fa-arrow-left" /> Mi trabajo
          </button>
          <span className="pag__breadcrumb-sep">/</span>
          <button type="button" className="pag__breadcrumb-link" onClick={() => setCurrentPath('')}>
            {expediente?.numero || `Expediente #${expediente?.id}`}
          </button>
          {getBreadcrumb().map((part, index) => (
            <React.Fragment key={index}>
              <span className="pag__breadcrumb-sep">/</span>
              {index === getBreadcrumb().length - 1 ? (
                <span className="pag__breadcrumb-current">{part}</span>
              ) : (
                <button
                  type="button"
                  className="pag__breadcrumb-link"
                  onClick={() => setCurrentPath(getBreadcrumb().slice(0, index + 1).join('/'))}
                >
                  {part}
                </button>
              )}
            </React.Fragment>
          ))}
        </nav>

        <div className="pag__toolbar">
          <div className="pag__search">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {canEditCurrent && (
            <>
              <button
                type="button"
                className="pag__tool-link"
                onClick={() => setShowNewFolderModal(true)}
                title="Nueva carpeta"
              >
                <i className="fas fa-folder-plus" /> Nueva carpeta
              </button>
              <button
                type="button"
                className="pag__tool-link"
                onClick={() => setShowUploadForm(true)}
                title="Subir archivo"
              >
                <i className="fas fa-arrow-up" /> Subir
              </button>
            </>
          )}
        </div>
      </div>

      <div className="pag__table-wrap">
        {(filteredFolders.length > 0 || filteredFiles.length > 0) ? (
          <table className="pag__table">
            <thead>
              <tr>
                <th className="pag__col-name">Archivo</th>
                <th className="pag__col-date">Modificado</th>
                <th className="pag__col-size">Tama&ntilde;o</th>
                <th className="pag__col-perm">Permisos</th>
                <th className="pag__th-actions" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {filteredFolders.map((folder, index) => (
                <tr
                  key={`folder-${index}`}
                  className="pag__tr"
                  onClick={() => navigateToFolder(folder.name)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <div className="pag__td-name">
                      <i className="fas fa-folder pag__icon pag__icon--folder" />
                      <span>{folder.name}</span>
                    </div>
                  </td>
                  <td className="pag__td-muted">{'\u2014'}</td>
                  <td className="pag__td-muted">{'\u2014'}</td>
                  <td className={`pag__td-perm ${canEditFolder(folder.name) ? 'pag__td-perm--edit' : ''}`}>
                    {canEditFolder(folder.name) ? 'Editable' : 'Solo lectura'}
                  </td>
                  <td className="pag__td-actions" />
                </tr>
              ))}

              {filteredFiles.map((file, index) => {
                const iconInfo = getFileIconClass(file.name);
                return (
                  <tr key={`file-${index}`} className="pag__tr">
                    <td onClick={() => handleFileClick(file)} style={{ cursor: 'pointer' }}>
                      <div className="pag__td-name">
                        <i className={`fas ${iconInfo.icon} pag__icon${iconInfo.tone ? ` pag__icon--${iconInfo.tone}` : ''}`} />
                        <span title={file.name}>{file.name}</span>
                      </div>
                    </td>
                    <td className="pag__td-muted">{formatDateShort(file.lastModified)}</td>
                    <td className="pag__td-muted">{file.size}</td>
                    <td className={`pag__td-perm ${file.canEdit ? 'pag__td-perm--edit' : ''}`}>
                      {file.canEdit ? 'Editable' : 'Solo lectura'}
                    </td>
                    <td className="pag__td-actions">
                      <div className="pag__actions">
                        {isVisualizableFile(file.name) && (
                          <button
                            type="button"
                            className="pag__action-btn"
                            onClick={() => handleFileClick(file)}
                            title="Ver"
                          >
                            <i className="fas fa-eye" />
                          </button>
                        )}
                        {file.canEdit && file.id && (
                          <button
                            type="button"
                            className="pag__action-btn"
                            onClick={() => handleUpdateProgress(file.id, file.name)}
                            title="Actualizar"
                          >
                            <i className="fas fa-pen" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="pag__action-btn"
                          onClick={() => handleFileClick(file)}
                          title={isVisualizableFile(file.name) ? 'Abrir' : 'Descargar'}
                        >
                          <i className="fas fa-download" />
                        </button>
                        {file.canEdit && (
                          <button
                            type="button"
                            className="pag__action-btn pag__action-btn--danger"
                            onClick={() => deleteFile(file)}
                            title="Eliminar"
                          >
                            <i className="fas fa-trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="pag__empty">
            <i className="fas fa-folder-open" />
            <p>{searchTerm ? 'Sin resultados para tu b\u00fasqueda' : 'Esta carpeta est\u00e1 vac\u00eda'}</p>
            {canEditCurrent && !searchTerm && (
              <button type="button" className="pag__btn-primary" onClick={() => setShowUploadForm(true)}>
                Subir archivo
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal subir archivos */}
      {showUploadForm && (
        <div className="pag-modal-overlay" onClick={() => setShowUploadForm(false)}>
          <div className="pag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pag-modal__header">
              <h3>Subir archivos</h3>
              <button type="button" className="pag-modal__close" onClick={() => setShowUploadForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleFileUpload}>
              <div className="pag-modal__body">
                <label className="pag-modal__label">Seleccionar archivos</label>
                <input
                  type="file"
                  className="pag-modal__input"
                  multiple
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                  required
                />
                {selectedFiles.length > 0 && (
                  <ul className="pag-file-list">
                    {selectedFiles.map((file, i) => (
                      <li key={i}>
                        {file.name}
                        <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="pag-modal__footer">
                <button type="button" className="pag__btn-ghost" onClick={() => setShowUploadForm(false)}>
                  Cancelar
                </button>
                <button type="submit" className="pag__btn-primary" disabled={uploading || selectedFiles.length === 0}>
                  {uploading ? 'Subiendo...' : 'Subir'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal nueva carpeta */}
      {showNewFolderModal && (
        <div className="pag-modal-overlay" onClick={() => setShowNewFolderModal(false)}>
          <div className="pag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pag-modal__header">
              <h3>Nueva carpeta</h3>
              <button type="button" className="pag-modal__close" onClick={() => setShowNewFolderModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateFolder}>
              <div className="pag-modal__body">
                <label className="pag-modal__label" htmlFor="newFolderName">Nombre de la carpeta</label>
                <input
                  id="newFolderName"
                  type="text"
                  className="pag-modal__input"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Ej: Planos revisados"
                  autoFocus
                  required
                />
              </div>
              <div className="pag-modal__footer">
                <button type="button" className="pag__btn-ghost" onClick={() => setShowNewFolderModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="pag__btn-primary" disabled={creatingFolder || !newFolderName.trim()}>
                  {creatingFolder ? 'Creando...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal edicion de parte */}
      {showEditForm && (
        <div className="pag-modal-overlay" onClick={() => setShowEditForm(false)}>
          <div className="pag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pag-modal__header">
              <h3>Editar parte</h3>
              <button type="button" className="pag-modal__close" onClick={() => setShowEditForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleEditParte}>
              <div className="pag-modal__body">
                <label className="pag-modal__label">Descripcion</label>
                <textarea
                  className="pag-modal__input"
                  rows="3"
                  value={editFormData.descripcion}
                  onChange={(e) => setEditFormData({ ...editFormData, descripcion: e.target.value })}
                  required
                />
                <label className="pag-modal__label" style={{ marginTop: 12 }}>Fecha limite</label>
                <input
                  type="date"
                  className="pag-modal__input"
                  value={editFormData.fechaLimite}
                  onChange={(e) => setEditFormData({ ...editFormData, fechaLimite: e.target.value })}
                  required
                />
                <label className="pag-modal__label" style={{ marginTop: 12 }}>Estado</label>
                <select
                  className="pag-modal__input"
                  value={editFormData.estado}
                  onChange={(e) => setEditFormData({ ...editFormData, estado: e.target.value })}
                  required
                >
                  <option value="PENDIENTE">Pendiente</option>
                  <option value="EN_PROGRESO">En proceso</option>
                  <option value="COMPLETADA">Completada</option>
                </select>
              </div>
              <div className="pag-modal__footer">
                <button type="button" className="pag__btn-ghost" onClick={() => setShowEditForm(false)}>Cancelar</button>
                <button type="submit" className="pag__btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal completar tarea */}
      {showCompleteModal && (
        <div className="pag-modal-overlay" onClick={() => setShowCompleteModal(false)}>
          <div className="pag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pag-modal__header">
              <h3>Completar tarea</h3>
              <button type="button" className="pag-modal__close" onClick={() => setShowCompleteModal(false)}>&times;</button>
            </div>
            <div className="pag-modal__body">
              <div className="pag-modal__warn">
                Esta accion no se puede deshacer. Ya no podras editar archivos en esta parte.
              </div>
              <p style={{ fontSize: 14, margin: '0 0 8px' }}>
                <strong>{parte.descripcion}</strong>
              </p>
              <p style={{ fontSize: 13, color: 'var(--pag-gray)', margin: 0 }}>
                {'\u00bf'}Confirmas que la tarea esta completada?
              </p>
              <label className="pag-modal__checkbox">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                No mostrar esta confirmacion de nuevo
              </label>
            </div>
            <div className="pag-modal__footer">
              <button type="button" className="pag__btn-ghost" onClick={() => setShowCompleteModal(false)}>
                Cancelar
              </button>
              <button type="button" className="pag__btn-primary" onClick={handleMarkAsCompleted}>
                Si, completar
              </button>
            </div>
          </div>
        </div>
      )}

      {showVersionControl && selectedArchivoId && (
        <VersionControl
          archivoId={selectedArchivoId}
          onVersionCreated={handleVersionCreated}
          onClose={handleCloseVersionControl}
        />
      )}
    </div>
  );
};

export default ParteAsignadaGestion;
